import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { debounce } from 'lodash';

import {
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import Grid from 'material/components/Grid';

import {
  deleteListGroup,
  fetchChildrenLists,
  fetchListGroups,
  fetchRootList,
  getContactsListFlatFileConfig,
  getContactsListFlatFilePlatformConfig,
  postListGroup,
  updateContactsList,
} from 'store/actions/contacts';
import { contactSelectors } from 'store/selectors/contacts';
import { organizationSelectors } from 'store/selectors/organizations';
import { useConfirm } from 'material-ui-confirm';

import { BreadcrumbsNavigation, Container, Dialog } from 'common/components';
import { CardWrapper, CircularProgressWrapper } from 'globalStyles';

import NewList from 'routes/Lists/components/NewList';
import Groups from 'routes/Lists/components/Groups';
import Lists from 'routes/Lists/components/Lists';
import ListCard from 'routes/Lists/components/ListCard';
import ListsTopCard from 'routes/Lists/components/ListsTopCard';

const ListsWrapper = styled.div`
  width: 100%;
  margin-bottom: 48px;
`;

const StyledListCard = styled(ListCard)`
  width: 100%;
  margin-top: 20px;
`;

const ADD_NEW_LIST_GROUP_DIALOG_TITLE = 'Enter a name and save the new group';
const ADD_TO_GROUP_DIALOG_TITLE = 'Select a group and add a list to it';

const ListsContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { organizationUuid } = useParams();
  const confirm = useConfirm();

  const {
    childrenLists,
    isChildrenListsFetching,
    isPending,
    organizationGroups,
    organization: { flags },
    rootList,
  } = useSelector(state => ({
    childrenLists: contactSelectors.getChildrenLists(state)[organizationUuid] || [],
    isChildrenListsFetching: contactSelectors.isChildrenListsFetching(state),
    isPending: contactSelectors.isPendingList(state),
    organization: organizationSelectors.getOrganization(state),
    organizationGroups: contactSelectors.getListGroups(state)[organizationUuid] || {},
    rootList: contactSelectors.getOrganizationRootList(state)[organizationUuid] || {},
  }));

  const [listTypeFilter, setListTypeFilter] = useState(0);
  const [showNewList, setShowNewList] = useState(false);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [newListGroup, setNewListGroup] = useState('');
  const [isNewListGroupDialogOpen, setIsNewListGroupDialogOpen] = useState(false);
  const [addToGroupData, setAddToGroupData] = useState({
    isModalOpen: false,
    listUuid: '',
    selectedGroup: '',
  });
  const [groupsData, setGroupsData] = useState({
    groups: {},
    isLoading: false,
    nonGeocoded: {},
    nonGrouped: [],
  });

  const groups = organizationGroups[rootList.uuid] || [];

  useEffect(() => {
    if (Object.keys(rootList).length === 0) {
      dispatch(fetchRootList(organizationUuid)).then(data => {
        if (childrenLists.length === 0) {
          dispatch(fetchChildrenLists(data.uuid, organizationUuid));
          dispatch(fetchListGroups(data.uuid, organizationUuid));
          dispatch(getContactsListFlatFileConfig(data.uuid));
          dispatch(getContactsListFlatFilePlatformConfig(data.uuid));
        }
      });
    } else if (childrenLists.length === 0) {
      dispatch(fetchChildrenLists(rootList.uuid, organizationUuid));
      dispatch(fetchListGroups(rootList.uuid, organizationUuid));
      dispatch(getContactsListFlatFileConfig(rootList.uuid));
      dispatch(getContactsListFlatFilePlatformConfig(rootList.uuid));
    }
  }, []);

  useEffect(() => {
    if (flags && !flags.showLists) {
      history.push(`/${organizationUuid}/dashboard`);
    }
  }, [history, flags, organizationUuid]);

  const groupUuidLists = childrenLists.reduce((acc, list) => {
    if (list.listGroups) {
      acc.push(...list.listGroups);
    }

    return acc;
  }, []);

  useEffect(() => {
    if (!isPending) {
      const groupsInitialArray = groups?.reduce(
        (acc, group) => {
          acc.groups[group.uuid] = {
            fields: [],
            name: group.name,
          };

          return acc;
        },
        {
          groups: {},
          isLoading: false,
          nonGeocoded: {},
          nonGrouped: [],
        }
      );
      const groupsOrderedData = childrenLists.reduce((acc, list) => {
        if (list.type === 'IsGeocodedList') {
          acc.nonGeocoded = list;
        } else if (list.listGroups?.length) {
          const groupUuid = list.listGroups[0];

          acc.groups[groupUuid] = {
            ...acc.groups[groupUuid],
            fields: [...(acc.groups[groupUuid]?.fields || []), list],
          };
        } else {
          acc.nonGrouped = [...acc.nonGrouped, list];
        }

        return acc;
      }, groupsInitialArray);

      setGroupsData(groupsOrderedData);
    }
  }, [groups.length, childrenLists.length, isPending, groupUuidLists.length]);

  const [searchInput, setSearchInput] = useState('');

  const debouncedSearch = useCallback(
    debounce(value => setSearchPhrase(value), 400),
    []
  );

  const handleSearchChange = event => {
    setSearchInput(event.target.value);
    debouncedSearch(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchPhrase('');
  };

  const handleCancelNewList = () => {
    setShowNewList(false);
  };

  const handleAddGroupDialogClose = () => {
    setNewListGroup('');
    setIsNewListGroupDialogOpen(false);
  };

  const handleAddGroup = async () => {
    setGroupsData(prevValue => ({
      ...prevValue,
      isLoading: true,
    }));
    await dispatch(postListGroup(rootList.uuid, { name: newListGroup }));
    await dispatch(fetchListGroups(rootList.uuid, organizationUuid));
    setGroupsData(prevValue => ({
      ...prevValue,
      isLoading: false,
    }));
    handleAddGroupDialogClose();
  };

  const handleOpenAddToGroupModal = (listUuid, listGroups) => {
    setAddToGroupData(prevValue => ({
      ...prevValue,
      isModalOpen: true,
      listUuid,
      selectedGroup: listGroups || '',
    }));
  };

  const handleAddToGroupDialogClose = async () => {
    setAddToGroupData(prevValue => ({
      ...prevValue,
      isModalOpen: false,
      selectedGroup: '',
    }));
  };

  const handleAddToGroup = async () => {
    setGroupsData(prevValue => ({
      ...prevValue,
      isLoading: true,
    }));
    await dispatch(
      updateContactsList(addToGroupData.listUuid, organizationUuid, {
        listGroups: [addToGroupData.selectedGroup],
      })
    );
    setGroupsData(prevValue => ({
      ...prevValue,
      isLoading: false,
    }));
    handleAddToGroupDialogClose();
  };

  const handleDeleteGroup = async (listGroupUuid, groupData) =>
    confirm({
      confirmationText: 'Delete',
      description: 'This will not delete any of the contact lists.',
      title: 'Delete Group?',
    })
      .then(async () => {
        setGroupsData(prevValue => ({
          ...prevValue,
          isLoading: true,
        }));
        await Promise.all(
          groupData.fields.map(list =>
            dispatch(updateContactsList(list.uuid, organizationUuid, { listGroups: [] }))
          )
        );
      })
      .then(() => {
        dispatch(deleteListGroup(rootList.uuid, listGroupUuid, organizationUuid));
      })
      .finally(() => {
        setGroupsData(prevValue => ({
          ...prevValue,
          isLoading: false,
        }));
      });

  const handleFilterList = lists =>
    lists.filter(
      list =>
        (listTypeFilter === 0 || list.type === listTypeFilter) &&
        list?.name?.toLowerCase().includes((searchPhrase || '').toLowerCase())
    );

  const breadcrumbs = [
    {
      link: `/${organizationUuid}/contacts/lists`,
      name: 'Lists',
    },
  ];

  return (
    <>
      <BreadcrumbsNavigation links={breadcrumbs} />
      <Container>
        <Grid container>
          <ListsTopCard
            childrenLists={childrenLists}
            listTypeFilter={listTypeFilter}
            onFilterChange={setListTypeFilter}
            onOpenNewList={() => setShowNewList(true)}
            onOpenNewGroupDialog={() => setIsNewListGroupDialogOpen(true)}
          />
        </Grid>
        <CardWrapper>
          <TextField
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search"
            variant="standard"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={!searchInput}
                      onClick={handleClearSearch}
                      aria-label="Clear search"
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <Tooltip title="Search">
                      <SearchIcon fontSize="small" />
                    </Tooltip>
                  </InputAdornment>
                ),
              },
            }}
          />
          {isChildrenListsFetching ? (
            <>
              <Typography color="textSecondary">Loading your contact lists...</Typography>
              <CircularProgressWrapper>
                <CircularProgress color="primary" size={60} />
              </CircularProgressWrapper>
            </>
          ) : (
            <ListsWrapper>
              {showNewList && (
                <NewList
                  list={rootList}
                  onClose={handleCancelNewList}
                  organizationUuid={organizationUuid}
                />
              )}
              <Groups
                groups={groupsData.groups}
                isLoading={groupsData.isLoading}
                isAddToGroupButtonDisabled={!groups.length}
                listType={listTypeFilter !== 0 ? listTypeFilter : undefined}
                onDeleteGroup={handleDeleteGroup}
                onOpenAddToGroupModal={handleOpenAddToGroupModal}
                searchPhrase={searchPhrase}
              />
              {listTypeFilter === 0 && !!Object.keys(rootList).length && (
                <StyledListCard countAll={rootList.count} list={rootList} />
              )}
              {listTypeFilter === 0 && !!Object.keys(groupsData.nonGeocoded).length && (
                <StyledListCard
                  countAll={groupsData.nonGeocoded.count}
                  list={groupsData.nonGeocoded}
                />
              )}
              <Lists
                isAddToGroupButtonDisabled={!groups.length}
                lists={handleFilterList(groupsData.nonGrouped)}
                onOpenAddToGroupModal={handleOpenAddToGroupModal}
              />
            </ListsWrapper>
          )}
        </CardWrapper>
        <Dialog
          handleConfirm={handleAddGroup}
          handleDialogClose={handleAddGroupDialogClose}
          isDialogOpen={isNewListGroupDialogOpen}
          title={ADD_NEW_LIST_GROUP_DIALOG_TITLE}
          disabledButton={!newListGroup || isPending}
        >
          <TextField
            fullWidth
            placeholder="group list name"
            onChange={event => setNewListGroup(event.target.value)}
            type="text"
            value={newListGroup}
          />
        </Dialog>
        <Dialog
          handleConfirm={handleAddToGroup}
          handleDialogClose={handleAddToGroupDialogClose}
          isDialogOpen={addToGroupData.isModalOpen}
          title={ADD_TO_GROUP_DIALOG_TITLE}
          disabledButton={!addToGroupData.selectedGroup || isPending}
        >
          <Select
            fullWidth
            value={addToGroupData.selectedGroup}
            onChange={event =>
              setAddToGroupData(prevValue => ({
                ...prevValue,
                selectedGroup: event.target.value,
              }))
            }
          >
            <MenuItem value="" />
            {groups?.map(group => (
              <MenuItem key={group.uuid} value={group.uuid}>
                {group.name}
              </MenuItem>
            ))}
          </Select>
        </Dialog>
      </Container>
    </>
  );
};

export default ListsContainer;
